/**
 * Menüwahl NACH der Buchung, Betreiber-Seite (sql/772, docs/KONZEPT-PAKET-MENUEWAHL.md Ablauf 4, Mockup Bild 4): im Vorgang
 * steht der Stand der Menüauswahl (gewählt oder offen, Zahlen je Menü) und der Knopf „Menüauswahl an Gast senden“ mit einer
 * Frist (Vorschlag: 14 Tage vor dem Termin). Ein Nachtrag ist nicht nötig: der Paketpreis bleibt gleich, nur ein Menü mit
 * Aufpreis ändert den Betrag. Die Karte erscheint nur, wenn die gebuchte Fassung eine Menüwahl hat.
 * Eigenständig (holt und schreibt ihren Stand selbst), damit die Vorgangsseite sie unverändert einhängen kann.
 */
import { useState } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Card, DateInput, Icon } from "./components";

export interface MenuewahlLage {
  verzeichnis: Record<string, { ids: string[]; soll: number; spaeter?: boolean }> | null;
  zeilen: { gruppe: string; text: string; gewaehlt: boolean }[];
  stand: { frist?: string | null; gesendetAm?: string | null; gewaehltAm?: string | null; revision?: number } | null;
  standardFrist: string;
}

const KLEIN = "font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim";
const datum = (iso: string | null | undefined) => {
  if (!iso) return "–";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: iso.length <= 10 ? "UTC" : "Europe/Berlin" });
};

/** Die Zeilen des Stands - rein, damit der Web-Test sie ohne Netz prüfen kann. */
export function menuewahlStandZeilen(d: MenuewahlLage): { status: string; wahl: { gruppe: string; text: string; gewaehlt: boolean }[]; gesendet: boolean; gewaehlt: boolean } {
  const s = d.stand ?? {};
  const gesendet = !!s.gesendetAm;
  const gewaehlt = !!s.gewaehltAm || (d.zeilen.length > 0 && d.zeilen.every((z) => z.gewaehlt));
  let status = "Noch nicht an den Gast gesendet.";
  if (gewaehlt) status = s.gewaehltAm ? `Der Gast hat die Menüs am ${datum(s.gewaehltAm)} festgelegt.` : "Die Menüs sind festgelegt.";
  else if (gesendet) status = `An den Gast gesendet am ${datum(s.gesendetAm)}. Frist: ${datum(s.frist)}. Noch offen.`;
  return { status, wahl: d.zeilen, gesendet, gewaehlt };
}

export function MenuewahlNachBuchungKarte({ eventId }: { eventId: string }) {
  const { data, reload } = useApi<MenuewahlLage>(`/api/events/${eventId}/menuewahl`);
  const [frist, setFrist] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState<{ ton: "ok" | "hinweis"; text: string } | null>(null);
  if (!data || !data.verzeichnis || Object.keys(data.verzeichnis).length === 0) return null;
  const z = menuewahlStandZeilen(data);
  const fristWert = frist ?? data.stand?.frist ?? data.standardFrist;

  async function senden() {
    setBusy(true);
    setMeldung(null);
    try {
      const r = await apiMutate<{ ok: boolean; frist: string; an: string }>(`/api/events/${eventId}/menuewahl/senden`, "POST", { frist: fristWert });
      setMeldung({ ton: "ok", text: `Gesendet an ${r.an}. Der Gast kann die Menüs bis ${datum(r.frist)} festlegen.` });
      reload();
    } catch (e) {
      setMeldung({ ton: "hinweis", text: e instanceof Error ? e.message : String(e) });
      reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5" data-testid="menuewahl-nach-buchung-karte">
      <div className="flex items-center gap-2">
        <Icon name="restaurant_menu" className="text-[19px] text-primary" />
        <h2 className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Menüauswahl</h2>
      </div>
      <p className={`mt-2 ${KLEIN}`} data-testid="menuewahl-status">
        {z.status}
      </p>
      <ul className="mt-2 grid gap-1">
        {z.wahl.map((w) => (
          <li key={w.gruppe} className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface" data-testid="menuewahl-stand-zeile" data-gewaehlt={w.gewaehlt ? "true" : "false"}>
            {w.gewaehlt ? w.text : "Menüauswahl offen"}
          </li>
        ))}
      </ul>
      {!z.gewaehlt && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="grid gap-1">
            <span className={KLEIN}>Frist für den Gast</span>
            <DateInput value={fristWert} onValueChange={setFrist} aria-label="Frist für die Menüauswahl" data-testid="menuewahl-frist" className="!w-auto" />
          </label>
          <Button type="button" variant="primary" disabled={busy || !fristWert} onClick={() => void senden()} data-testid="menuewahl-senden">
            {z.gesendet ? "Menüauswahl erneut senden" : "Menüauswahl an Gast senden"}
          </Button>
        </div>
      )}
      <p className={`mt-2 ${KLEIN}`}>Ohne Nachtrag: der Preis des Pakets bleibt gleich. Nur ein Menü mit Aufpreis ändert den Betrag, das läuft dann automatisch wie bei einem Nachtrag. Verpasst der Gast die Frist, entsteht eine Aufgabe.</p>
      {meldung && (
        <p className={`mt-2 ${KLEIN} ${meldung.ton === "ok" ? "!text-success" : ""}`} role="status" data-testid="menuewahl-meldung-betrieb">
          {meldung.text}
        </p>
      )}
    </Card>
  );
}
